import * as React from "react";
import { Input, Form } from "antd";
import { ColumnProps } from "antd/lib/table";
import { Table, TableProps } from "Components/General/Table";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const EditableContext = React.createContext<any>({});

const EditableRow = ({ index: _, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  record: {};
  render?: (text: string, record: {}, index: number) => React.ReactNode;
  editable: boolean;
  dataIndex: string;
  title: string;
  className?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputComponent?: React.ForwardRefExoticComponent<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputComponentProps?: any;
  handleSave: (record: {}) => void;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editable,
  title,
  children,
  className,
  record,
  render,
  dataIndex,
  handleSave,
  inputComponent: InputComponent,
  inputComponentProps,
  ...restProps
}) => {
  const [editing, setEditing] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const inputRef = React.useRef<any>();
  const form = React.useContext(EditableContext);

  React.useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;
  if (editable) {
    childNode = editing ? (
      <Form.Item style={{ margin: 0 }} name={dataIndex}>
        {InputComponent ? (
          <InputComponent {...inputComponentProps} ref={inputRef} onPressEnter={save} onBlur={save} />
        ) : (
          <Input ref={inputRef} onPressEnter={save} onBlur={save} />
        )}
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingRight: 24 }} onClick={toggleEdit}>
        {render ? render(record[dataIndex], record, 1) : children}
      </div>
    );
  }
  return (
    <td title={title} className={className} {...restProps}>
      <div>{childNode}</div>
    </td>
  );
};

interface EditableCellTableProps<T> extends Omit<TableProps, "rowKey"> {
  dataSource: T[];
  rowKey: keyof T;
  columns: EditableCellTableColumnProps<T>[];
  onEdit: (record: T) => void;
  showEditableCell?: boolean;
}

interface EditableCellTableState<T> {
  dataSource: T[];
}

export interface EditableCellTableColumnProps<T = {}> extends ColumnProps<T> {
  editable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputComponent?: React.ForwardRefExoticComponent<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputComponentProps?: any;
}

export class EditableCellTable<T> extends React.Component<EditableCellTableProps<T>, EditableCellTableState<T>> {
  constructor(props: EditableCellTableProps<T>) {
    super(props);
    this.state = {
      dataSource: props.dataSource,
    };
  }
  componentDidUpdate(prevProps: Readonly<EditableCellTableProps<T>>): void {
    if (prevProps.dataSource !== this.props.dataSource) {
      this.setState({ dataSource: this.props.dataSource });
    }
  }

  handleSave = (row) => {
    const newData = [...this.state.dataSource];
    const index = newData.findIndex((item) => row[this.props.rowKey] === item[this.props.rowKey]);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    this.setState({ dataSource: newData }, () => {
      this.props.onEdit(row);
    });
  };

  render() {
    const { dataSource: _dataSource, rowKey, onEdit: _onEdit, columns, ...tableProps } = this.props;
    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };

    const editableColumns = columns.map((col) => {
      if (!col.editable) {
        return col;
      }

      return {
        ...col,
        onCell: (record) => ({
          record,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: this.handleSave,
          render: col.render,
          inputComponent: col.inputComponent,
          inputComponentProps: col.inputComponentProps,
        }),
      };
    });

    return (
      <div>
        <Table<any> // eslint-disable-line @typescript-eslint/no-explicit-any
          {...tableProps}
          components={components}
          rowClassName={() => (this.props.showEditableCell ? "editable-row-toggle" : "editable-row")}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          dataSource={this.state.dataSource as any}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          columns={editableColumns as any}
          rowKey={rowKey as string}
        />
      </div>
    );
  }
}
